import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { useShallow } from "zustand/react/shallow";

import { AllCommunityModule, type StateUpdatedEvent, type GridApi,type GridReadyEvent, type RowDoubleClickedEvent, type CellStyle } from "ag-grid-community";
import { AgGridProvider } from "ag-grid-react";
const modules = [AllCommunityModule];

import { Button, FormControl, Input, MenuItem, Select, Tooltip } from "@mui/material";
import { Dialog, DialogTitle, DialogContent, DialogActions, IconButton } from "@mui/material";

import kutil, { type PageInfo } from "@modules/kutil";
import commonCode, { type Code } from "@modules/common_code";
import valueFormatter from "@modules/value_formatter";
import { useStableHandlers } from "@/modules/stable_handlers";

import { CustomAgGridReact, type CustomColDef, type CustomColGroupDef } from "@/components/aggrid/custom_ag_grid_react";

import { useAuthStore } from "@user/store/auth_store";
import { useSettingsStore } from "@user/store/settings_store";

import "./popup_temp_work.scoped.scss";

import TrSnChangePopup from "@user/views/inoutbound/popup_tr_sn_change";

interface State {
  gridApi: GridApi | null,
  trSnInfo: object,
  isShowTrSnChange: boolean
}

const defState: State = {
  gridApi: null,
  trSnInfo: {},
  isShowTrSnChange: false
};

interface SearchParams {
  workReqEtcNo: string,
  wh: string,
  pn: string,
  trNoLike: string,
  snLike: string,
  woLike: string,
  workShopLike: string
  requesterLike: string
}

const defSearchParams: SearchParams = {
  workReqEtcNo: "",
  wh: "",
  pn: "",
  trNoLike: "",
  snLike: "",
  woLike: "",
  workShopLike: "",
  requesterLike: ""
};

interface Result {
  info: any,
  workReqErpInfos: any[]
}

const defResult = {
  info: {},
  workReqErpInfos: []
};

const validationMsg = [
  {id: "trSn", type: "NotBlank", msg: "TR No, S/N를 선택해 주세요."},
  {id: "workReqEtcNo", type: "InvalidStatus", msg: "이미 매칭완료된 작업입니다."},
  {id: "eaiId", type: "InvalidStatus", msg: (_p: any, e: any) => `${e.desc}`}
];

function PopupTempWork(props: any) {
  const [state, setState] = useState<State>(kutil.deepCopy(defState));
  const [searchParams, setSearchParams] = useState<SearchParams>(kutil.deepCopy(defSearchParams));
  const [result, setResult] = useState<Result>(kutil.deepCopy(defResult));

  const auth = useAuthStore(useShallow((state) => state.getAuth(props.id)));
  const gridStateKey = "/inoutbound/popup_temp_work";
  const gridState = useMemo(() => useSettingsStore.getState().getGridState(gridStateKey), []);

  const searchParamsRef = useRef(searchParams);
  const updateSearchParams = useCallback((patch: Partial<SearchParams>, isReset: boolean = false) => {
    if (isReset) {
      searchParamsRef.current = { ...patch } as SearchParams;
    } else {
      searchParamsRef.current = { ...searchParamsRef.current, ...patch };
    }
    setSearchParams(searchParamsRef.current);
    return searchParamsRef.current;
  }, []);

const tmpCellStyle1: CellStyle = {textAlign: "center", cursor: "pointer"};
  const gridInfo = useMemo((): (CustomColDef | CustomColGroupDef)[] => [
    { headerName: "작업구분", field: "inoutType", width: 100, cellStyle: {textAlign: "center"}, valueFormatter: valueFormatter.commonCode("inoutType") },
    { headerName: "상세구분", field: "inoutDetailType", width: 100, cellStyle: {textAlign: "center"}, valueFormatter: valueFormatter.commonCode("inoutDetailType") },
    { headerName: "창고", field: "wh", width: 100, cellStyle: {textAlign: "center"}, valueFormatter: valueFormatter.commonCode("wh") },
    { headerName: "작업상태", field: "workReqStatus", width: 100, cellStyle: {textAlign: "center"}, valueFormatter: valueFormatter.commonCode("workReqStatus") },
    { headerName: "Creat Date", field: "createDate", width: 200, cellStyle: {textAlign: "center"}, valueFormatter: valueFormatter.date("yyyy-MM-dd HH:mm:ss") },
    { headerName: "P/N", headerClass: "hd-ta-left", field: "pn", width: 150, cellStyle: { textAlign: "left"} },
    { headerName: "TR No", headerClass: "hd-ta-left", field: "trNo", width: 150, cellStyle: {textAlign: "left"} },
    { headerName: "S/N", field: "sn", headerClass: "hd-ta-left", width: 150, cellStyle: {textAlign: "left"} },
    { headerName: "TR/SN 변경", field: "", width: 120, cellStyle: kutil.aggridButtonCellStyle,
      cellRenderer: (p: any) => {
        return (<button className="icon-button grid-write" onClick={() => updateTrSn(p.data)}>변경</button>);
      }
    },
    { headerName: "KWD", headerClass: "hd-ta-left", field: "kwd", width: 150, cellStyle: {textAlign: "left"} },
    { headerName: "CTG", headerClass: "hd-ta-left", field: "ctg", width: 120, cellStyle: {textAlign: "left"} },
    { headerName: "Work Shop", headerClass: "hd-ta-left", field: "workShop", width: 150, cellStyle: { textAlign: "left"} },
    { headerName: "W/O", headerClass: "hd-ta-left", field: "wo", width: 150, cellStyle: { textAlign: "left"} },
    { headerName: "Visit No", headerClass: "hd-ta-left", field: "visitNo", width: 150, cellStyle: { textAlign: "left"} },
    { headerName: "기번(HL)", field: "hl", width: 120, cellStyle: {textAlign: "center"} },
    { headerName: "Request Qty", headerClass: "hd-ta-right", field: "reqQty", width: 110, cellStyle: {textAlign: "right"}, valueFormatter: valueFormatter.number },
    { headerName: "UOM", field: "uom", width: 80, cellStyle: {textAlign: "center"} },
    { headerName: "요청자", field: "requester", width: 100, cellStyle: tmpCellStyle1 }
  ], []);

  ////////////////////////////////////////////////////////////////////////////////
  // grid 관련 기능들
  ////////////////////////////////////////////////////////////////////////////////
  const gridOptions = useMemo(() => ({
    rowSelection: {
      mode: "singleRow" as const,
      checkboxes: false,
      enableClickSelection: true
    },
  }), []);
  const gridCallbacks = useStableHandlers({
    onGridReady: (e: GridReadyEvent) => {
      setState(prev => ({ ...prev, gridApi: e.api }));
    },
    onStateUpdated: (e: StateUpdatedEvent) => {
      kutil.onStateUpdated(e, gridStateKey);
    },
    onRowDoubleClicked: (_e: RowDoubleClickedEvent) => {
      save();
    }
  });

  useEffect(() => {
    init();
  }, []);

  const isInit = useRef(false);
  async function init() {
    try {
      if (isInit.current == false) {
        isInit.current = await initData();
      }
      if (isInit.current) {
        await initParams();
        await doSearch();
      }
    } finally {
      kutil.setLoadingStatus(false);
    }
  }

  async function initData() {
    return true;
  }

  async function initParams() {
    const searchParams = kutil.deepCopy(defSearchParams);
    searchParams.workReqEtcNo = props.data.workReqEtcNo;
    searchParams.wh = props.data.wh;
    searchParams.pn = props.data.pn;
    updateSearchParams(searchParams);
  }

  async function doSearch() {
    const searchParams = searchParamsRef.current;

    try {
      const reqSearchParams = kutil.copyObj(searchParams, {} as SearchParams);
      const data = await kutil.sendGet("/api/inoutbound/temp_work_list.data?mode=searchDetail", reqSearchParams, {});
      setResult({
        info: data.info,
        workReqErpInfos: data.workReqErpInfos,
      });
    } catch(e) {
      console.log(e);
    }
  }

  async function closePopup() {
    props.onClose();
  }

  async function save() {
    const selectedInfos = state.gridApi!.getSelectedRows();
    if (selectedInfos.length == 0) {
      kutil.alert("작업을 선택해 주세요.");
      return;
    }

    const reqInfo = selectedInfos[0];
    reqInfo.workReqEtcNo = result.info.workReqEtcNo;
    reqInfo.trSn = (reqInfo.trNo || "") + (reqInfo.sn || "");
    if (!reqInfo.trSn) {
      kutil.alert("TR No, S/N를 선택해 주세요.");
      return;
    }

    const confirmMsg = result.info.completeQty != reqInfo.reqQty ? "작업 지시 수량과 작업 수량이 다릅니다. 계속 진행하시겠습니까?" : "매칭하시겠습니까?";
    if (await kutil.confirm(confirmMsg) == false) {
      return false;
    }

    try {
      const data = await kutil.sendPost("/api/inoutbound/temp_work_list.proc?mode=execIfMatch", reqInfo, {
        validationMsg: validationMsg
      });
      await kutil.alert("매칭하였습니다.");
      props.onClose({isSuccess: true});
    } catch(e) {
      console.log(e);
    }
  }

  async function updateTrSn(info: any) {
    openPopupTrSnChange(info);
  }

  async function openPopupTrSnChange(info: any) {
    setState(prev => ({ ...prev, isShowTrSnChange: true, trSnInfo: info}));
  }

  async function closePopupTrSnChange(data: any) {
    setState(prev => ({ ...prev, isShowTrSnChange: false }));

    if (data?.isSuccess) {
      doSearch();
    }
  }

  return (
    <>
      {/* 팝업 */}
      <Dialog
        sx={{ "& .MuiDialog-container": { "& .MuiPaper-root": { width: "100%", maxWidth: "1580px", }, }, }}
        aria-labelledby="customized-dialog-title"
        open={props.isShow}
      >
        <DialogTitle sx={{ m: 0, p: 2 }} id="customized-dialog-title">
          임시작업 관리
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={closePopup}
          sx={(theme) => ({
            position: "absolute",
            right: 36,
            top: 24,
            color: theme.palette.grey[500],
          })}
        >
        </IconButton>
        <DialogContent>
          {/* 팝업 내용 */}
          <div className="popup-contents">
            {/* 작업정보 */}
            <div className="table-wrap">
              <table className="tbl-list">
                <thead>
                  <tr>
                    <th scope="col">Work Date</th>
                    <th scope="col">작업구분</th>
                    <th scope="col">창고</th>
                    <th scope="col">P/N</th>
                    <th scope="col">TR No</th>
                    <th scope="col">S/N</th>
                    <th scope="col">KWD</th>
                    <th scope="col">CTG</th>
                    <th scope="col">W/O</th>
                    <th scope="col">Work Shop</th>
                    <th scope="col">Work Qty</th>
                    <th scope="col">UOM</th>
                    <th scope="col">요청자</th>
                    <th scope="col">작업자</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>{kutil.date2string(result.info.workCompleteDate, "yyyy-MM-dd")}</td>
                    <td>{commonCode.getCodeValue("inoutType", result.info.inoutType)}</td>
                    <td>{commonCode.getCodeValue("wh", result.info.wh)}</td>
                    <td>{ result.info.pn }</td>
                    <td>{ result.info.trNo }</td>
                    <td>{ result.info.sn }</td>
                    <td>{ result.info.kwd }</td>
                    <td>{ result.info.ctg }</td>
                    <td>{ result.info.wo }</td>
                    <td>{ result.info.workShop }</td>
                    <td>{ valueFormatter.number(result.info.completeQty) }</td>
                    <td>{ result.info.uom }</td>
                    <td>{ result.info.requesterName }</td>
                    <td>{ result.info.workOrdererName }</td>
                  </tr>
                </tbody>
              </table>
            </div>
            {/*// 작업정보 */}
            {/* 검색 */}
            {result.info.erpMatchStatus !== "C" ? (
            <div className="srch-area mid">
              <div>
                <p>창고</p>
                <Input
                  inputProps={{ readOnly: true }}
                  value={commonCode.getCodeValue("wh", searchParams.wh)}
                />
              </div>
              <div>
                <p>P/N</p>
                <Input
                  inputProps={{ readOnly: true }}
                  value={searchParams.pn}
                  onChange={(e) => {updateSearchParams({ pn: e.target.value })}}
                  onKeyDown={(e) => {kutil.onKeyDownForInput(e, doSearch)}}
                  placeholder="P/N"
                />
              </div>
              <div>
                <p>TR No</p>
                <Input
                  inputProps={{ maxLength: 100 }}
                  value={searchParams.trNoLike}
                  onChange={(e) => {updateSearchParams({ trNoLike: e.target.value })}}
                  onKeyDown={(e) => {kutil.onKeyDownForInput(e, doSearch)}}
                  placeholder="TR No"
                />
              </div>
              <div>
                <p>S/N</p>
                <Input
                  inputProps={{ maxLength: 100 }}
                  value={searchParams.snLike}
                  onChange={(e) => {updateSearchParams({ snLike: e.target.value })}}
                  onKeyDown={(e) => {kutil.onKeyDownForInput(e, doSearch)}}
                  placeholder="S/N"
                />
              </div>
              <div>
                <p>W/O</p>
                <Input
                  inputProps={{ maxLength: 100 }}
                  value={searchParams.woLike}
                  onChange={(e) => {updateSearchParams({ woLike: e.target.value })}}
                  onKeyDown={(e) => {kutil.onKeyDownForInput(e, doSearch)}}
                  placeholder="W/O"
                />
              </div>
              <div>
                <p>Work Shop</p>
                <Input
                  inputProps={{ maxLength: 100 }}
                  value={searchParams.workShopLike}
                  onChange={(e) => {updateSearchParams({ workShopLike: e.target.value })}}
                  onKeyDown={(e) => {kutil.onKeyDownForInput(e, doSearch)}}
                  placeholder="Work Shop"
                />
              </div>
              <div>
                <p>요청자</p>
                <Input
                  inputProps={{ maxLength: 100 }}
                  value={searchParams.requesterLike}
                  onChange={(e) => {updateSearchParams({ requesterLike: e.target.value })}}
                  onKeyDown={(e) => {kutil.onKeyDownForInput(e, doSearch)}}
                  placeholder="요청자"
                />
              </div>
              <p>
                <Tooltip title="검색">
                  <button className="icon-button popup-search" onClick={doSearch}>검색</button>
                </Tooltip>
              </p>
            </div>
            ) : (
            <div className="srch-area mid">
            </div>
            )}
            {/*// 검색 */}
            {/* 그리드 */}
            <AgGridProvider modules={modules}>
              <div className="grid-area">
                <CustomAgGridReact
                  columnDefs={gridInfo}
                  rowData={result.workReqErpInfos}
                  defaultColDef={kutil.defaultColDef}
                  initialState={gridState}
                  rowSelection={gridOptions.rowSelection}
                  onGridReady={gridCallbacks.onGridReady}
                  onStateUpdated={gridCallbacks.onStateUpdated}
                  onRowDoubleClicked={gridCallbacks.onRowDoubleClicked}
                />
              </div>
            </AgGridProvider>
            {state.isShowTrSnChange && <TrSnChangePopup isShow={state.isShowTrSnChange} onClose={closePopupTrSnChange} id={props.id} data={{path: "temp_work_list", info: state.trSnInfo}} />}
          </div>
          {/*// 팝업 내용 */}
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" autoFocus onClick={closePopup}>
            {result.info.erpMatchStatus !== "C" ? "취소" : "닫기"}
          </Button>
          {result.info.erpMatchStatus !== "C" && auth.write &&
          <Button variant="contained" autoFocus onClick={save}>
            ERP 매칭
          </Button>
          }
        </DialogActions>
      </Dialog>
      {/*// 팝업 */}
    </>
  );
}

export default React.memo(PopupTempWork);
